import React, { useId } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { normalizePortalText } from 'utils/baochauTheme';
import './style.scss';

const FormItem = ({
  className,
  label,
  hiddenLabel = false,
  isRequired = false,
  children,
  disabled,
  display,
  style,
  variant,
  readOnly = false,
  helperText,
  controlId,
  linkLabelHref,
  labelHref,
  isBlankHref = true,
}) => {
  const generatedId = useId().replace(/:/g, '');
  const childField = React.isValidElement(children) ? children.props?.field : undefined;
  const resolvedControlId = controlId || (childField ? `bw-field-${String(childField).replace(/[^a-zA-Z0-9_-]/g, '-')}` : `bw-field-${generatedId}`);
  const helperId = helperText ? `${resolvedControlId}-hint` : undefined;
  const wrapperStyle = style && typeof style === 'object' ? style : undefined;
  const isSoftStyle = variant === 'gray';
  const canReceiveControlState =
    React.isValidElement(children) &&
    (typeof children.type !== 'string' || ['input', 'select', 'textarea'].includes(children.type));
  const resolvedChildren = canReceiveControlState
    ? React.cloneElement(children, {
        id: children.props.id || resolvedControlId,
        disabled: disabled || children.props.disabled,
        readOnly: readOnly || children.props.readOnly,
        'aria-required': children.props['aria-required'] ?? (isRequired || undefined),
        'aria-describedby': [children.props['aria-describedby'], helperId].filter(Boolean).join(' ') || undefined,
      })
    : children;

  return (
    <div
      className={classNames('bw_form_field', className, {
        bw_frm_box: true,
        bw_disable: disabled,
        bw_readonly: readOnly,
        'bw_form_field--soft': isSoftStyle,
        'is-disabled': disabled,
        'is-readonly': readOnly,
      })}
      style={wrapperStyle}
      aria-disabled={disabled || undefined}>
      <div>
        {!hiddenLabel && (
          <label className='bw_form_field__label' htmlFor={resolvedControlId}>
            <span>{typeof label === 'string' ? normalizePortalText(label) : label}</span>
            {isRequired ? (
              <span className='bw_form_field__required' aria-hidden='true'>
                *
              </span>
            ) : null}
            {linkLabelHref ? (
              <a
                className='bw_form_field__label_link'
                target={isBlankHref ? '_blank' : ''}
                rel={isBlankHref ? 'noreferrer' : undefined}
                href={linkLabelHref}>
                {typeof labelHref === 'string' ? normalizePortalText(labelHref) : labelHref}
              </a>
            ) : null}
          </label>
        )}
        <div className='bw_form_field__control' style={{ display: display ?? 'block' }}>
          {resolvedChildren}
        </div>
        {helperText ? (
          <p id={helperId} className='bw_form_field__hint'>
            {typeof helperText === 'string' ? normalizePortalText(helperText) : helperText}
          </p>
        ) : null}
      </div>
    </div>
  );
};

FormItem.propTypes = {
  label: PropTypes.string,
  hiddenLabel: PropTypes.bool,
  isRequired: PropTypes.bool,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  helperText: PropTypes.node,
  controlId: PropTypes.string,
  style: PropTypes.object,
  variant: PropTypes.string,
};

export default FormItem;
